// useAuditData.js
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config.json";


export const useAuditData = ({ externalFyId = null, answerFilters = {},  auditType = null } = {}  ) => {
  const [auditItems,     setAuditItems]     = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [locations,      setLocations]      = useState([]);
  const [selectedFY,     setSelectedFY]     = useState(null);
  const [isLoading,      setIsLoading]      = useState(false);
  const [error,          setError]          = useState(null);

  const abortRef  = useRef(false);
  const filterStr = JSON.stringify(answerFilters);
  useEffect(() => () => { abortRef.current = true; }, []);

  /* Derived summary */
  const summary = useMemo(() => ({
    total:    auditItems.length,
    pending:  auditItems.filter(i => i.auditStatus === "PENDING" || i.auditStatus === "IN_REVIEW").length,
    inReview: auditItems.filter(i => i.auditStatus === "IN_REVIEW").length,
    approved: auditItems.filter(i => ["APPROVED","ACCEPTED"].includes(i.auditStatus)).length,
    rejected: auditItems.filter(i => i.auditStatus === "REJECTED").length,
  }), [auditItems]);

  const buildBody = useCallback((fyId, filters = {}) => {
    const body = { financialYearId: fyId, frameworkIds: [] };
    if (filters.locationIds?.length) body.locationIds = filters.locationIds;
    if (auditType) {
      body.auditType = auditType;
    }
    return body;
  }, []);

  /**
   * Fetch KPI list AND cumulative values.
   * We fetch quantitative-data in bulk for all KPIs to compute cumulativeValue
   * so each KPI row can show the sum without a separate API call per KPI.
   */
  const fetchItems = useCallback(async (fyId, filters = {}) => {
    /* Step 1: get KPI assignments */
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getAssignedAuditorKpis`,
      {},
      buildBody(fyId, filters),
      "POST",
      false, false
    );
    if (!isSuccess) return [];

    const kpiList = data?.data || [];
    if (!kpiList.length) return [];

    return kpiList.map((item) => ({ ...item }));
  }, [buildBody]);


  useEffect(() => {
    (async () => {
      try {
        const [fyRes, locRes] = await Promise.all([
          apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`, {}, {}, "GET", false, false),
          apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`,        {}, {}, "GET", false, false),
        ]);
        if (abortRef.current) return;

        const fyList = fyRes.data?.data || [];
        setFinancialYears(fyList);
        setLocations((locRes.data?.data || []).map((l) => ({ id: String(l.id), name: l.unitCode })));

        if (!selectedFY) {
          const fy = externalFyId
            ? fyList.find((f) => String(f.id) === String(externalFyId))
            : fyList[fyList.length - 1];
          if (fy) setSelectedFY({ id: fy.id, name: fy.financial_year_value, financial_year_value: fy.financial_year_value });
        }
      } catch {
        if (!abortRef.current) setError("Setup failed");
      }
    })();
  }, [externalFyId]); // eslint-disable-line

  /* 2. Load KPI data — reactive to FY / filter change */
  useEffect(() => {
    if (!selectedFY?.id) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchItems(selectedFY.id, answerFilters).then(items => {
      if (cancelled || abortRef.current) return;
      setAuditItems(items);
    }).catch(() => {
      if (!cancelled) setError("Failed to load audit data");
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [selectedFY?.id, filterStr]); // eslint-disable-line

  const refetch = useCallback(() => {
    if (!selectedFY?.id) return Promise.resolve();
    return fetchItems(selectedFY.id, answerFilters).then(items => {
      if (!abortRef.current) setAuditItems(items);
    });
  }, [selectedFY?.id, filterStr, fetchItems, auditType]); // eslint-disable-line

  return {
    auditItems,
    summary,
    isLoading,
    error,
    financialYears,
    selectedFY,
    setSelectedFY: useCallback(fy => setSelectedFY(fy), []),
    locations,
    refetch,
  };
};